import { useTranslator } from '@picsart/rc/services/localization';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Dynamic from '@picsart/editorlayout/Dynamic';
import DynamicHeader from '@picsart/editorlayout/DynamicHeader';
import DynamicBody from '@picsart/editorlayout/DynamicBody';
import { v4 as uuid } from 'uuid';
import { FileRejection } from 'react-dropzone';

import useDoProcess from 'hooks/useDoProcess';
import { WatermarksPositions } from 'pages/Batch/utils/watermark/configs';
import useUploadNotification from 'pages/Batch/utils/useUploadNotification';
import analytics, { OriginEnum, SubToolNames } from 'pages/Batch/utils/analytics';
import useHeaderStyles from 'pages/Batch/styles';
import { BATCH_ACTIONS, BATCH_ERROR, BATCH_TOOLS } from 'pages/Batch/configs';
import batchSlice from 'store/batch';
import { IUserWatermark } from 'types/store';
import errorLogger from 'services/errorLogger';

import WatermarkPosition from './WatermarkPosition';
import WatermarkOpacity from './WatermarkOpacity';
import WatermarkLogos from './WatermarkLogos';
import WatermarkSize from './WatermarkSize';
import watermarkGeneratorInit from './utils/watermarkGeneratorInit';
import { WATERMARK_ANGLES_CONFIG, DEFAULT_WATERMARK_ATTRS } from './constants';
import WatermarkRotate from './WatermarkRotate/WatermarkRotate';

const WatermarkTool: FC = () => {
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [watermarkAttrs, setWatermarkAttrs] = useState(DEFAULT_WATERMARK_ATTRS);

  const dispatch = useDispatch();
  const translate = useTranslator();
  const doProcess = useDoProcess();
  const headerClasses = useHeaderStyles();
  const { showUploaderError } = useUploadNotification();

  const clearLogoFromImage = () => {
    setSelectedLogoId(null);
    setWatermarkAttrs(DEFAULT_WATERMARK_ATTRS);
    doProcess(BATCH_ACTIONS.watermark, { position: null });
  };

  const handleLogoUpload = useCallback(async (files: File[], rejectedFiles: FileRejection[]) => {
    analytics.uploadDone(OriginEnum.batch_editor, rejectedFiles.length ? 'fail' : 'success');
    if (rejectedFiles.length) {
      showUploaderError(BATCH_ERROR.fileFormat);
      return;
    }

    try {
      const id = uuid();
      setSelectedLogoId(id);
      const logoImgUrl = URL.createObjectURL(files[0]);

      await watermarkGeneratorInit(logoImgUrl);
      dispatch(batchSlice.actions.setUserWatermarks({ url: logoImgUrl, id }));
    } catch (err) {
      showUploaderError(BATCH_ERROR.serverError);
      errorLogger.trigger(err, 'error');
    }
  }, [showUploaderError, dispatch]);

  const handleLogoUploadCancel = useCallback(() => analytics.uploadDone(OriginEnum.batch_editor, 'cancel'), []);

  const handleLogoDelete = (el: IUserWatermark) => {
    if (el.id === selectedLogoId) {
      clearLogoFromImage();
    }
    dispatch(batchSlice.actions.deleteWatermark(el.id));
  };

  const handleLogoSelect = async (el: IUserWatermark) => {
    try {
      setSelectedLogoId(el.id);
      await watermarkGeneratorInit(el.url);
    } catch (err) {
      showUploaderError(BATCH_ERROR.serverError);
      errorLogger.trigger(err, 'error');
    }
  };

  const handleLogoRemove = () => {
    clearLogoFromImage();
  };

  const handleLogoPositionChange = (position: WatermarksPositions) => {
    const rotateAngle = WATERMARK_ANGLES_CONFIG[position] || watermarkAttrs.rotateAngle;

    analytics.utils().setSubTool(BATCH_TOOLS.watermark, {
      name: SubToolNames.position,
      value: position,
    });

    setWatermarkAttrs(prevState => ({ ...prevState, position, rotateAngle, hideStatusIndicator: true }));
  };

  const handleLogoOpacityChange = async (value: number) => {
    const opacityValue = value * 5;

    analytics.utils().setSubTool(BATCH_TOOLS.watermark, {
      name: SubToolNames.opacity,
      value: `${opacityValue}%`,
    });

    setWatermarkAttrs(prevState => ({ ...prevState, opacity: opacityValue, hideStatusIndicator: true }));
  };

  const handleLogoSizeChange = (value: number) => {
    analytics.utils().setSubTool(BATCH_TOOLS.watermark, {
      name: SubToolNames.size,
      value: `${value}%`,
    });

    setWatermarkAttrs(prevState => ({ ...prevState, size: value, hideStatusIndicator: true }));
  };

  const handleLogoRotateAngleChange = (value: number) => {
    analytics.utils().setSubTool(BATCH_TOOLS.watermark, {
      name: SubToolNames.rotate,
      value: `${value}°`,
    });
    setWatermarkAttrs(prevState => ({ ...prevState, rotateAngle: value, hideStatusIndicator: true }));
  };

  useEffect(() => {
    if (selectedLogoId) {
      doProcess(BATCH_ACTIONS.watermark, watermarkAttrs);
    }
  // TODO/doProcess in dependency arr creates infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLogoId, watermarkAttrs]);

  return (
    <Dynamic
      header={(
        <DynamicHeader
          className={headerClasses.customHeader}
          title={translate('Watermark')}
        />
      )}
      body={(
        <DynamicBody>
          <WatermarkLogos
            selectedLogoId={selectedLogoId}
            handleLogoRemove={handleLogoRemove}
            handleLogoDelete={handleLogoDelete}
            handleLogoSelect={handleLogoSelect}
            handleLogoUpload={handleLogoUpload}
            handleLogoUploadCancel={handleLogoUploadCancel}
          />
          <WatermarkPosition selectedPosition={watermarkAttrs.position} selectedLogoId={selectedLogoId} handleChange={handleLogoPositionChange} />
          <WatermarkOpacity opacity={watermarkAttrs.opacity} disabled={!selectedLogoId} handleChange={handleLogoOpacityChange} />
          <WatermarkSize size={watermarkAttrs.size} disabled={!selectedLogoId} handleChange={handleLogoSizeChange} />
          <WatermarkRotate rotateAngle={watermarkAttrs.rotateAngle} disabled={!selectedLogoId} handleChange={handleLogoRotateAngleChange} />
        </DynamicBody>
      )}
    />
  );
};

export default memo(WatermarkTool);
